/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import Logo from '@/components/Logo';
import { toast } from 'sonner';
import { Mail, Sparkles, ArrowLeft, MailCheck } from 'lucide-react';
import { motion } from 'framer-motion';
import { requestMagicLink } from '@/lib/magicLink';

const MagicLinkRequestPage = () => {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [devUrl, setDevUrl] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    const em = email.trim();
    if (!em) return;
    setLoading(true);
    try {
      const res = await requestMagicLink(em);
      setSent(true);
      if (res.dev_magic_url) setDevUrl(res.dev_magic_url);
      toast.success(t('magicLinkSent') || 'Se l\'indirizzo è registrato, riceverai un link di accesso.');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Errore invio email';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background bg-hero-gradient flex items-center justify-center px-4 py-8 relative">
      <div className="absolute inset-0 bg-grain opacity-30 pointer-events-none" />
      <motion.div
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.24 }}
        className="w-full max-w-md relative"
      >
        <div className="flex items-center justify-between mb-6">
          <Logo size={44} withText textClassName="text-base" tagline={t('tagline')} />
        </div>

        <Card className="p-6 rounded-2xl shadow-elev-lg" data-testid="magic-link-card">
          {!sent ? (
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="h-5 w-5 text-primary" />
                <h1 className="font-display font-semibold text-xl">Accedi senza password</h1>
              </div>
              <p className="text-sm text-muted-foreground mb-5">
                Ti inviamo un link magico via email. Un click e sei dentro — nessuna password richiesta.
              </p>
              <form onSubmit={submit} className="space-y-4" data-testid="magic-link-request-form">
                <div>
                  <Label htmlFor="ml-email" className="text-xs text-muted-foreground">Email</Label>
                  <div className="mt-1 relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="ml-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-9"
                      placeholder="nome@esempio.it"
                      autoComplete="email"
                      required
                      data-testid="magic-link-email-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading || !email.trim()}
                  data-testid="magic-link-submit-btn"
                >
                  {loading ? 'Invio in corso…' : 'Inviami il link magico'}
                </Button>
              </form>
              <div className="mt-5 text-sm text-center text-muted-foreground">
                <Link to="/login" className="inline-flex items-center gap-1 text-primary font-medium hover:underline" data-testid="magic-link-back-to-login">
                  <ArrowLeft className="h-3.5 w-3.5" /> Accedi con password
                </Link>
              </div>
            </div>
          ) : (
            <div className="text-center py-4" data-testid="magic-link-sent-panel">
              <div className="mx-auto w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <MailCheck className="h-7 w-7 text-primary" />
              </div>
              <h2 className="font-display font-semibold text-lg mb-2">Controlla la tua email</h2>
              <p className="text-sm text-muted-foreground mb-4">
                Se <span className="font-semibold text-foreground">{email}</span> è registrato, riceverai un link di accesso valido per 15 minuti.
              </p>
              {devUrl && (
                <div className="mt-4 p-3 rounded-lg border border-amber-300 bg-amber-50 text-left" data-testid="magic-link-dev-panel">
                  <p className="text-[11px] uppercase font-semibold text-amber-700 mb-1">Dev mode</p>
                  <a href={devUrl} className="text-xs text-amber-800 break-all hover:underline" data-testid="magic-link-dev-url">
                    {devUrl}
                  </a>
                </div>
              )}
              <div className="mt-6">
                <button
                  onClick={() => { setSent(false); setDevUrl(null); }}
                  className="text-sm text-primary hover:underline font-medium"
                  data-testid="magic-link-resend-btn"
                >
                  Invia di nuovo
                </button>
              </div>
            </div>
          )}
        </Card>

        <div className="mt-6 text-center text-xs text-muted-foreground">
          Lingua: {i18n.language?.toUpperCase()}
        </div>
      </motion.div>
    </div>
  );
};

export default MagicLinkRequestPage;
